import React from "react";
import Menu from "@mui/material/Menu";
import { Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { IconDownload, IconSettings, IconRemoveStory, IconShare, IconStory, IconTrash } from "../../utils/icons";
import { downloadOptions } from "./constants";

interface Props {
    anchorEl?: null | HTMLElement;
    onClose?: (e: any) => void;
    title: string;
    id: string;
    downloadUrl?: string;
    showBelow?: boolean;
    onDownloadImage: (type: string) => void;
    onShare?: () => void;
    onEdit?: () => void;
    onAddToStory?: () => void;
    onRemoveFromStory?: () => void;
    onDelete?: () => void;
    onAddToVideoShortlist?: () => void;
    onRemoveFromVideoShortlist?: () => void;
    showReviewVideo?: boolean;
    onReviewVideo?: () => void;
    showDownloadOptions?: boolean;
    showDelete?: boolean;
    onUnapprove?: () => void;
    showUnapprove?: boolean;
    unapproveDisabled?: boolean;
    unapproveLabel?: string;
    onCopyPath?: () => void;
    onReimport?: () => void;
    onViewPreviousVersions?: () => void;
}

export function MenuList(props: Props) {
    const {
        onViewPreviousVersions,
        title,
        id,
        downloadUrl,
        showBelow,
        onShare,
        onEdit,
        onAddToStory,
        onRemoveFromStory,
        onDelete,
        onDownloadImage,
        ...rest
    } = props;

    const getMenuOption = (Icon: any, text: string, callback?: any, disabled?: boolean) => {
        if (!callback) return null;

        return (
            <MenuItem
                className="!p-3 !px-4 flex gap-2 items-center min-w-[220px]"
                disabled={disabled}
                onClick={(e) => {
                    if (!disabled) {
                        callback();
                        rest.onClose?.(e);
                    }
                }}
                sx={{
                    opacity: disabled ? 0.3 : 1,
                    cursor: disabled ? "not-allowed" : "pointer",
                    // the icon stroke rides on currentColor: SVG presentation attributes cannot read var()
                    color: "var(--muted)"
                }}
            >
                <Icon width={24} height={24} size={24} stroke="currentColor" />
                <Typography color="var(--text-2)">{text}</Typography>
            </MenuItem>
        );
    };

    return (
        <Menu
            {...rest}
            id="single-video-menu"
            aria-labelledby={id || "single-video-menu-btn"}
            open={!!rest.anchorEl}
            className="py-2"
            anchorOrigin={{
                vertical: showBelow ? "bottom" : "top",
                horizontal: "left"
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "right"
            }}
        >
            {props.showDownloadOptions &&
                downloadUrl &&
                downloadOptions.map((btn) => getMenuOption(IconDownload, btn.text, () => onDownloadImage(btn.type)))}
            {getMenuOption(IconShare, `Share ${title}`, onShare)}
            {getMenuOption(IconSettings, "Settings", onEdit)}
            {getMenuOption(IconStory, "View Previous Versions", onViewPreviousVersions)}
            {getMenuOption(IconShare, "Copy Path", props.onCopyPath)}
            {getMenuOption(IconSettings, "Re-import file", props.onReimport)}
            {getMenuOption(IconStory, "Add to story", onAddToStory)}
            {getMenuOption(IconStory, "Add to video shortlist", props.onAddToVideoShortlist)}
            {getMenuOption(IconRemoveStory, "Remove from this shortlist", props.onRemoveFromVideoShortlist)}
            {getMenuOption(IconRemoveStory, "Remove video from this story", onRemoveFromStory)}
            {/* Delete hidden for Video Tab*/}
            {props.showDelete !== false && getMenuOption(IconTrash, `Delete`, onDelete)}
            {props.showUnapprove &&
                !props.unapproveDisabled &&
                getMenuOption(IconSettings, props.unapproveLabel || "Unapprove video", props.onUnapprove)}
            {/* Conditionally render Review Video menu item for unapproved variations */}
            {props.showReviewVideo && (
                <MenuItem
                    className="!p-3 !px-4 flex gap-2 items-center min-w-[220px]"
                    onClick={(e) => {
                        props.onReviewVideo?.();
                        rest.onClose?.(e);
                    }}
                    sx={{ color: "var(--muted)" }}
                >
                    <IconStory width={24} height={24} stroke="currentColor" />
                    <Typography color="var(--text-2)">Review</Typography>
                </MenuItem>
            )}
        </Menu>
    );
}
